import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { getLayer } from '../services/api';

interface PropertyMapProps {
  listings: any[];
  center?: [number, number];
  zoom?: number;
}

// Custom SVG house icon
function createHouseIcon(status: 'pass' | 'near'): L.DivIcon {
  const colors = {
    pass: { fill: 'rgba(99, 102, 241, 0.3)', stroke: 'rgba(165, 180, 252, 0.95)' },
    near: { fill: 'rgba(245, 158, 11, 0.25)', stroke: 'rgba(253, 230, 138, 0.95)' },
  };
  const c = colors[status] || colors.pass;

  return L.divIcon({
    className: `custom-marker custom-marker--${status}`,
    html: `<div class="realestate-marker">
      <svg viewBox="0 0 24 26" width="24" height="26">
        <path d="M12 1L1 11v14h6v-8h10v8h6V11L12 1z" 
              fill="${c.fill}" stroke="${c.stroke}" stroke-width="1.5"/>
      </svg>
    </div>`,
    iconSize: [24, 26],
    iconAnchor: [12, 26],
    popupAnchor: [0, -26],
  });
}

const LAYER_CONFIG: Record<string, { label: string; checked: boolean }> = {
  noise: { label: 'Lärm', checked: false },
  green: { label: 'Grünflächen', checked: false },
  schools: { label: 'Schulen', checked: false },
  culture: { label: 'Kultur', checked: false },
  oldtown: { label: 'Altstadt', checked: false },
};

export default function PropertyMap({ listings, center, zoom }: PropertyMapProps) {
  const mapRef = useRef<L.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const markersRef = useRef<L.LayerGroup | null>(null);
  const layersRef = useRef<Record<string, L.LayerGroup>>({});
  const [activeLayers, setActiveLayers] = useState<Record<string, boolean>>({});

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = L.map(mapContainerRef.current, {
      center: center || [47.3769, 8.5417], // Zurich
      zoom: zoom || 8,
      zoomControl: true,
    });

    // Dark CartoDB tiles (matching the old project's aesthetic)
    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
      subdomains: 'abcd',
      maxZoom: 19,
    }).addTo(map);

    markersRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Update markers when listings change
  useEffect(() => {
    if (!mapRef.current || !markersRef.current) return;

    markersRef.current.clearLayers();
    const bounds: L.LatLng[] = [];

    for (const listing of listings) {
      const lat = parseFloat(listing.latitude || listing.lat);
      const lng = parseFloat(listing.longitude || listing.lng);
      if (!lat || !lng || lat === 0 || lng === 0) continue;

      const status = listing.status || 'pass';
      const icon = createHouseIcon(status);
      const marker = L.marker([lat, lng], { icon });

      // Popup
      const price = listing.price_chf
        ? `CHF ${Number(listing.price_chf).toLocaleString('de-CH')}`
        : 'N/A';

      marker.bindPopup(`
        <div class="map-popup">
          <strong>${listing.title || 'Immobilie'}</strong><br/>
          <span>${listing.city || ''} ${listing.canton ? `(${listing.canton})` : ''}</span><br/>
          <span>${price} · ${listing.rooms || '?'} Zi. · ${listing.living_space_m2 || '?'} m²</span><br/>
          <span class="popup-score">Score: <strong>${listing.score || '-'}</strong></span>
          ${listing.source_url ? `<br/><a href="${listing.source_url}" target="_blank" rel="noopener">Zum Inserat →</a>` : ''}
        </div>
      `);

      markersRef.current!.addLayer(marker);
      bounds.push(L.latLng(lat, lng));
    }

    // Auto-fit bounds
    if (bounds.length > 0) {
      mapRef.current.fitBounds(L.latLngBounds(bounds), { padding: [50, 50], maxZoom: 13 });
    }
  }, [listings]);

  // Toggle GIS layer
  async function toggleLayer(name: string) {
    if (!mapRef.current) return;

    const isActive = activeLayers[name];

    if (isActive) {
      // Remove layer
      if (layersRef.current[name]) {
        mapRef.current.removeLayer(layersRef.current[name]);
        delete layersRef.current[name];
      }
      setActiveLayers(prev => ({ ...prev, [name]: false }));
    } else {
      // Load and add layer
      try {
        const geojson = await getLayer(name);
        const layerGroup = L.geoJSON(geojson, {
          style: getLayerStyle(name),
          pointToLayer: (feature, latlng) => {
            return L.circleMarker(latlng, {
              radius: 5,
              fillColor: getLayerColor(name),
              color: getLayerColor(name),
              weight: 1,
              fillOpacity: 0.7,
            });
          },
          onEachFeature: (feature, layer) => {
            if (feature.properties?.name) {
              layer.bindPopup(feature.properties.name);
            }
          },
        }).addTo(mapRef.current!);

        layersRef.current[name] = layerGroup;
        setActiveLayers(prev => ({ ...prev, [name]: true }));
      } catch (err) {
        console.error(`Failed to load layer ${name}:`, err);
      }
    }
  }

  return (
    <div className="map-wrapper">
      <div ref={mapContainerRef} className="map-container" />

      {/* Layer controls */}
      <div className="layer-controls">
        <div className="layer-controls-title">Ebenen</div>
        {Object.entries(LAYER_CONFIG).map(([name, cfg]) => (
          <label key={name} className="layer-checkbox">
            <input
              type="checkbox"
              checked={!!activeLayers[name]}
              onChange={() => toggleLayer(name)}
            />
            <span>{cfg.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function getLayerStyle(name: string): L.PathOptions {
  switch (name) {
    case 'green': return { fillColor: '#22c55e', fillOpacity: 0.35, color: '#22c55e', weight: 1 };
    case 'oldtown': return { fillColor: '#f59e0b', fillOpacity: 0.15, color: '#f59e0b', weight: 2, dashArray: '4,4' };
    default: return { fillColor: '#6366f1', fillOpacity: 0.3, color: '#6366f1', weight: 1 };
  }
}

function getLayerColor(name: string): string {
  switch (name) {
    case 'green': return '#22c55e';
    case 'schools': return '#ec4899';
    case 'culture': return '#a855f7';
    case 'oldtown': return '#f59e0b';
    default: return '#6366f1';
  }
}
